import Vue from 'vue';
import Vuex from 'vuex';
import './plugins/vuetify';
import App from './App.vue';
import router from './router';
import './registerServiceWorker';
import Vuetify from 'vuetify';
import 'vuetify/dist/vuetify.min.css';
import {AssessmentInProgress} from '@/state/models/AssessmentInProgress';
import {Answer} from '@/state/models/Answer';
import {TeacherHelperApi} from '@/TeacherHelperApi';
import {AssessmentHistory} from '@/state/models/AssessmentHistory'; // Ensure you are using css-loader
import {Settings} from '@/state/models/Settings';
import {Class} from '@/state/models/Class';
import {Student} from '@/state/models/Student';
import {Test} from '@/state/models/Test';
import {QuestionPassFail} from '@/state/models/QuestionPassFail';
import {Question} from '@/state/models/Question';

Vue.config.productionTip = false;
Vue.use(Vuetify);
Vue.use(Vuex);

const api: TeacherHelperApi = new TeacherHelperApi();
const store: any = new Vuex.Store({
    state: {
        toolbar: {
            title: 'Teacher Helper',
            subTitle: undefined,
            hideSearch: true,
            showBackButton: false,
            backButtonRoute: undefined
        },
        settings: undefined,
        assessments: [] as Test[],
        assessmentsInProgress: [] as AssessmentInProgress[],
        assessmentHistory: [] as AssessmentHistory[],
        questions: [] as Question[],
        students: [] as Student[],
        classes: [] as Class[],
    },
    mutations: {
        setToolbarOptions(state, toolbarOptions) {
            state.toolbar.title = toolbarOptions.title;
            state.toolbar.subTitle = toolbarOptions.subTitle;
            state.toolbar.hideSearch = toolbarOptions.hideSearch;
            state.toolbar.showBackButton = toolbarOptions.showBackButton;
            state.toolbar.backButtonRoute = toolbarOptions.backButtonRoute;
        },
        hideSearch(state, hideFlag) {
            state.toolbar.hideSearch = hideFlag;
        },
        answerQuestion(state, answerInfo) {
            const assessment = state.assessments.find(a => a.assessmentId === answerInfo.assessmentId);
            const assessmentInProgress = state.assessmentsInProgress.find(
                a => a.assessmentId === answerInfo.assessmentId && a.studentId === answerInfo.studentId);

            if (assessment && assessmentInProgress) {
                const hasQuestion = assessment.questions.find((q: any) => q.id === answerInfo.questionId);
                if (hasQuestion) { // found the question in the template of the test
                    // find the question in the question list to get the correct answer
                    const question =
                        state.questions.find((q: Question) => q.id === answerInfo.questionId);
                    let answer = assessmentInProgress.answerMap.get(answerInfo.questionId);
                    if (!answer) {
                        answer = new Answer();
                    }
                    // record the provided answer, and the correct answer. they can be compared later for grading
                    answer.answer = answerInfo.answer;
                    answer.correctAnswer = question ? question.correctAnswer : undefined;
                    assessmentInProgress.answerMap.set(answerInfo.questionId, answer);
                }
            }
        },
        beginTest(state, testInfo) {
            const index = state.assessmentsInProgress.findIndex(
                (a) => a.assessmentId === testInfo.testId && a.studentId === testInfo.studentId);
            if (index === -1) {
                state.assessmentsInProgress.push(new AssessmentInProgress(testInfo.testId, testInfo.studentId));
            }
        },
        loadSettings(state, settings) {
            this.settings = settings;
        },
        loadAssessments(state, assessments) {
            state.assessments = assessments;
        },
        loadQuestions(state, questions) {
            state.questions = questions;
        },
        loadHistory(state, history) {
            state.assessmentHistory = history;
        },
        loadStudents(state, students) {
            state.students = students;
        },
        loadClasses(state, classes) {
            state.classes = classes;
        },
        addHistoryEntry(state, historyEntry) {
            state.assessmentHistory.push(historyEntry);
        }
    },
    actions: {
        getSettings(context) {
            return api.getSettings().then((s: Settings) => {
                context.commit('loadSettings', s);
                return s;
            });
        },
        getAssessments(context, filter: { ids: number[] }) {
            return api.getAssessments(filter ? filter.ids : []).then((r) => {
                context.commit('loadAssessments', r);
                return r;
            });
        },
        searchAssessments(context) {
            return api.searchAssessments().then((r) => {
                context.commit('loadAssessments', r);
                return r;
            });
        },
        getQuestions(context, filter: { ids: number[] }) {
            return filter && filter.ids ?
                api.getQuestionsByIds(filter.ids).then((r) => {
                    context.commit('loadQuestions', r);
                    return r;
                }) :
                Promise.reject('Questions can only be requested by id');
        },
        getHistory(context, filter: { ids: number[] }) {
            return filter && filter.ids ?
                api.getHistory(filter.ids).then((r) => {
                    context.commit('loadHistory', r);
                    return r;
                }) :
                Promise.reject('Search history instead of requesting all');
        },
        searchHistory(context, filter: {
            studentIds?: number[],
            maxNumEntries: number | undefined,
            startDate: Date | undefined,
            endDate: Date | undefined
        }) {
            return api.searchHistory(
                filter.studentIds,
                filter.maxNumEntries || undefined,
                filter.startDate || undefined,
                filter.endDate || undefined).then((r) => {
                context.commit('loadHistory', r);
                return r;
            });
        },
        getStudents(context, filter: { ids: number[] }) {
            return api.getStudents(filter && filter.ids ? filter.ids : []).then((sList) => {
                context.commit('loadStudents', sList);
                return sList;
            });
        },
        searchStudents(context, filter: {searchQ: string}) {
            return api.searchStudents(filter && filter.searchQ ? filter.searchQ : '')
                .then((sList) => {
                    context.commit('loadStudents', sList);
                    return sList;
                });
        },
        getClasses(context, filter: { ids: number[] }) {
            return api.getClasses(filter && filter.ids ? filter.ids : []).then((cList) => {
                context.commit('loadClasses', cList);
                return cList;
            });
        },
        searchClasses(context, filter: {studentIds: undefined}) {
            return api.searchClasses(filter && filter.studentIds ? filter.studentIds : []);
        },
        finishTest(context, testInfo) {
            const testToFinishIndex = context.state.assessmentsInProgress.findIndex(
                a => a.assessmentId === testInfo.assessmentId &&
                    a.studentId === testInfo.studentId);
            if (testToFinishIndex > -1) {
                // found the test to finish. remove from the list
                const testToFinish: AssessmentInProgress = context.state.assessmentsInProgress[testToFinishIndex];
                // remove the test from the list of tests in progress
                context.state.assessmentsInProgress.splice(testToFinishIndex, 1);

                return api.postHistory(
                    new AssessmentHistory(
                        new Date(),
                        testToFinish.assessmentId as number,
                        testToFinish.studentId as number,
                        testToFinish.answerMap))
                    .then((r) => {
                        context.commit('addHistoryEntry', r);
                        return r;
                    });
            }
        },
    },
},);


new Vue({
    store,
    router,
    render: (h) => h(App),
}).$mount('#app');
